import type { Day, Frequency, QuoteInfo, Reader, ScheduledRunOccurrence } from 'apiConnector/scheduledRun/model';
import type { EditRunResponse, Run, ValidateQuotesResult } from 'apiConnector/scheduledRun/scheduledRuns';
import type { Collection } from 'state/utils/types';
import { TIME_ZONE } from 'utils/core/date';
import type { TypedError } from 'utils/errors/errors';
import type { LoadableContent } from 'utils/status';
export const TRIGGER_MODES = ['range', 'days'] as const;
export type TriggerMode = (typeof TRIGGER_MODES)[number];
export type ScheduleOperation = 'updateQuotes' | 'editSchedule';
export type ConfirmBannerType = 'update' | 'pause' | 'reschedule' | 'delete';

export type OccurrenceReloadPendingQuote = {
  quoteId: string;
  tabId: string;
};

export type OccurrenceReloadState = {
  status: 'idle' | 'loading' | 'success';
  operationId?: string;
  tabIds: readonly string[];
  queuedQuoteIds: readonly string[];
  queuedQuoteIdsByTabId: Readonly<Record<string, readonly string[]>>;
  pendingByTemporaryFormId: Readonly<Record<string, OccurrenceReloadPendingQuote>>;
  succeededQuoteIds: readonly string[];
  failedQuoteIds: readonly string[];
  skippedQuoteIds: readonly string[];
  skippedCount: number;
};

export interface ScheduledRunFields {
  time?: string;
  frequency: Frequency;
  range?: { startDate: string; endDate: string };
  title?: string;
  days: readonly Day[];
  readers: readonly Reader[];

  comment?: string;
}
export type SelectableRun = Run & {
  isSelected?: boolean;
};
export interface ScheduledRun extends ScheduledRunFields {
  pristineFields: (keyof ScheduledRunFields)[];
  quotes: readonly QuoteInfo[];

  type?: ScheduleOperation;
  triggerMode: TriggerMode;
  existingTitle?: string;
  runs: LoadableContent<readonly SelectableRun[], TypedError>;

  createRun: LoadableContent<EditRunResponse | undefined, TypedError>;

  updateRun: LoadableContent<undefined, TypedError>;

  deleteRun: LoadableContent<undefined, TypedError>;

  addQuote: LoadableContent<ValidateQuotesResult | undefined, TypedError>;

  occurrenceModalOpen: boolean;
  occurrences: LoadableContent<readonly ScheduledRunOccurrence[], TypedError>;
  occurrenceDetail: LoadableContent<ScheduledRunOccurrence, TypedError>;
  selectedOccurrenceId?: string;
  occurrenceRequestId?: string;
  occurrenceRunId?: string;
  occurrenceDetailRequestId?: string;
  occurrenceDetailOccurrenceId?: string;
  occurrenceReload: OccurrenceReloadState;

  confirmBanner?: ConfirmBannerType;
}
export type Payoffs = Record<string, Record<string, boolean>>;
export type ScheduledRunState = {
  timeZone: string;
  runs: Collection<ScheduledRun>;
  payoffs: Payoffs;
};

export const SCHEDULED_FIELDS: readonly (keyof ScheduledRunFields)[] = [
  'days',
  'range',
  'time',
  'title',
  'frequency',
  'readers',
  'comment',
];
export const defaultFrequency = () => ({ value: undefined, type: 'Days' as const });
export const initialOccurrenceReloadState: OccurrenceReloadState = {
  status: 'idle',
  tabIds: [],
  queuedQuoteIds: [],
  queuedQuoteIdsByTabId: {},
  pendingByTemporaryFormId: {},
  succeededQuoteIds: [],
  failedQuoteIds: [],
  skippedQuoteIds: [],
  skippedCount: 0,
};
export const initScheduleState = (): ScheduledRun => ({
  triggerMode: 'range',
  readers: [],
  quotes: [],
  days: [],
  frequency: defaultFrequency(),
  pristineFields: [...SCHEDULED_FIELDS],
  runs: { status: 'idle' },
  createRun: { status: 'idle' },
  updateRun: { status: 'idle' },
  deleteRun: { status: 'idle' },
  addQuote: { status: 'idle' },
  occurrenceModalOpen: false,
  occurrences: { status: 'idle' },
  occurrenceDetail: { status: 'idle' },
  occurrenceReload: initialOccurrenceReloadState,
});
export const initialScheduledRunState: ScheduledRunState = { timeZone: TIME_ZONE, runs: {}, payoffs: {} };
